import { createListCollection, Field as ChakraField, Fieldset, Input, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import MultiSelect from '@components/ui/multiSelect/MultiSelect';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { PlatformSelectors } from '@redux/config/config.selectors';
import { RootState } from '@redux/store';
import { haveSameUniqueElements } from '@utils/common.utils';
import { Field, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { Organization } from '@/types/config/config.types';

export default function OrganizationGeneralForm() {
  const { initialValues, values, errors, touched, setFieldValue } = useFormikContext<Partial<Organization>>();
  const canAccessPlatform = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_PLATFORM));
  const platforms = useAppSelector(PlatformSelectors.selectAllPlatforms);

  return (
    <CockpitFormLayout title="components.organizations.general.title">
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <VStack gap={5}>
          <ChakraField.Root variant="cockpit" required invalid={touched.name && !!errors.name}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.organizations.general.name" />
              <RequiredIndicator />
              {initialValues.name !== values.name && <ModifiedTag />}
            </ChakraField.Label>
            <Field as={Input} type="text" name="name" id="name" />
          </ChakraField.Root>
          <ChakraField.Root variant="cockpit" required invalid={touched.email && !!errors.email}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.organizations.general.email" />
              <RequiredIndicator />
              {initialValues.email !== values.email && <ModifiedTag />}
            </ChakraField.Label>
            <Field as={Input} type="text" name="email" id="email" />
          </ChakraField.Root>
          {canAccessPlatform && (
            <ChakraField.Root variant="cockpit">
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.organizations.general.platforms" />
                <RequiredIndicator />
                {!haveSameUniqueElements(initialValues.platforms!, values.platforms!) && <ModifiedTag />}
              </ChakraField.Label>
              <MultiSelect
                values={values.platforms?.map((platform) => platform.code) ?? []}
                label="components.organizations.general.addPlatforms"
                collection={createListCollection({
                  items: platforms.map((platform) => ({
                    label: platform.name,
                    value: platform.code,
                  })),
                })}
                onChange={(values) =>
                  setFieldValue(
                    'platforms',
                    platforms.filter((platform) => values.includes(platform.code)),
                  )
                }
              />
            </ChakraField.Root>
          )}
        </VStack>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
